import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { notificationAPI } from '../../utils/api';
import './Navbar.css';

export default function Navbar() {
  const { user, logout, isAdmin, isDecorator, unreadCount, setUnreadCount } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef(null);

  useEffect(() => { setMenuOpen(false); setUserMenuOpen(false); }, [location.pathname]);

  // Scroll effect
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close user menu on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Fetch notifications
  useEffect(() => {
    if (!user) return;
    const fetchUnread = async () => {
      try {
        const { data } = await notificationAPI.getAll({ limit: 1 });
        setUnreadCount(data.unreadCount || 0);
      } catch (_) { }
    };
    fetchUnread();
    const interval = setInterval(fetchUnread, 15000);
    return () => clearInterval(interval);
  }, [user?._id]);

  const handleLogout = () => { logout(); navigate('/'); };

  const getDashboardLink = () => {
    if (isAdmin) return '/admin';
    if (isDecorator) return '/decorator/dashboard';
    return '/dashboard';
  };

  const getDashboardLabel = () => {
    if (isAdmin) return '🛡️ Admin Panel';
    if (isDecorator) return '🎨 My Studio';
    return '📋 Dashboard';
  };

  const isActive = (path) => location.pathname.startsWith(path);

  return (
    <>
      <nav className={`nb-navbar ${scrolled ? 'nb-scrolled' : ''}`}>
        {/* Gold line top */}
        <div className="nb-top-line" />

        <div className="nb-inner">
          {/* ── LOGO ── */}
          <Link to="/" className="nb-logo">
            <div className="nb-logo-icon">
              <span>🎊</span>
              <div className="nb-logo-glow" />
            </div>
            <div className="nb-logo-text">
              <span className="nb-logo-main">DecorHub</span>
              <span className="nb-logo-city">INDIA</span>
            </div>
          </Link>

          {/* ── CENTER LINKS ── */}
          <div className="nb-center-links">
            <Link to="/decorators" className={`nb-link ${isActive('/decorators') ? 'nb-link-active' : ''}`}>
              <span className="nb-link-icon">🎨</span>
              <span>Decorators</span>
              {isActive('/decorators') && <div className="nb-link-bar" />}
            </Link>

            {user && (
              <>
                <Link to={getDashboardLink()} className={`nb-link ${isActive(getDashboardLink()) ? 'nb-link-active' : ''}`}>
                  <span className="nb-link-icon">📋</span>
                  <span>Dashboard</span>
                  {isActive(getDashboardLink()) && <div className="nb-link-bar" />}
                </Link>

                <Link to="/chat" className={`nb-link ${isActive('/chat') ? 'nb-link-active' : ''}`}>
                  <span className="nb-link-icon">💬</span>
                  <span>Chat</span>
                  {isActive('/chat') && <div className="nb-link-bar" />}
                </Link>
                <Link to="/faq" className="faq-nav-link">
                  <span className="faq-nav-icon">💡</span>
                  <span className="faq-nav-text">How It Works</span>
                  <span className="faq-nav-dot" />
                </Link>

              </>
            )}
          </div>

          {/* ── RIGHT SIDE ── */}
          <div className="nb-right">
            {user ? (
              <>
                {/* Notification Bell */}
                <Link to="/notifications" className="nb-bell">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                    <path d="M13.73 21a2 2 0 0 1-3.46 0" />
                  </svg>
                  {unreadCount > 0 && (
                    <span className="nb-bell-badge">
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                  <div className="nb-bell-ring" style={{ animationPlayState: unreadCount > 0 ? 'running' : 'paused' }} />
                </Link>

                {/* User Menu */}
                <div className="nb-user-wrap" ref={userMenuRef}>
                  <button
                    className={`nb-user-btn ${userMenuOpen ? 'nb-user-btn-active' : ''}`}
                    onClick={() => setUserMenuOpen(o => !o)}
                  >
                    <div className="nb-avatar">
                      {user.name?.charAt(0).toUpperCase()}
                    </div>
                    <span className="nb-user-name">{user.name?.split(' ')[0]}</span>
                    <svg className={`nb-chevron ${userMenuOpen ? 'nb-chevron-up' : ''}`} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <polyline points="6 9 12 15 18 9" />
                    </svg>
                  </button>

                  {/* Dropdown */}
                  {userMenuOpen && (
                    <div className="nb-dropdown">
                      <div className="nb-dropdown-header">
                        <div className="nb-dropdown-avatar">{user.name?.charAt(0).toUpperCase()}</div>
                        <div>
                          <div className="nb-dropdown-name">{user.name}</div>
                          <div className="nb-dropdown-role">
                            {isAdmin ? '🛡️ Admin' : isDecorator ? '🎨 Decorator' : '👤 Client'}
                          </div>
                        </div>
                      </div>

                      <div className="nb-dropdown-divider" />

                      <Link to={getDashboardLink()} className="nb-dropdown-item">
                        <span>📋</span> {getDashboardLabel()}
                      </Link>
                      <Link to="/notifications" className="nb-dropdown-item">
                        <span>🔔</span> Notifications
                        {unreadCount > 0 && <span className="nb-dropdown-badge">{unreadCount}</span>}
                      </Link>
                      <Link to="/chat" className="nb-dropdown-item">
                        <span>💬</span> Chat
                      </Link>
                      <Link to="/faq" className="nb-dropdown-item"> <span>🤔</span>How It Works</Link>

                      <div className="nb-dropdown-divider" />

                      <button className="nb-dropdown-item nb-dropdown-logout" onClick={handleLogout}>
                        <span>🚪</span> Logout
                      </button>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div className="nb-auth-btns">
                <Link to="/login" className="nb-login-btn">Login</Link>
                <Link to="/register" className="nb-register-btn">
                  <span>Register</span>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <polyline points="9 18 15 12 9 6" />
                  </svg>
                </Link>
              </div>
            )}

            {/* Hamburger */}
            <button
              className={`nb-hamburger ${menuOpen ? 'nb-hamburger-open' : ''}`}
              onClick={() => setMenuOpen(o => !o)}
              aria-label="Menu"
            >
              <span /><span /><span />
            </button>
          </div>
        </div>
      </nav>

      {/* ── MOBILE MENU ── */}
      <div className={`nb-mobile-menu ${menuOpen ? 'nb-mobile-open' : ''}`}>
        <div className="nb-mobile-inner">
          {user && (
            <div className="nb-mobile-user">
              <div className="nb-mobile-avatar">{user.name?.charAt(0).toUpperCase()}</div>
              <div>
                <div className="nb-mobile-uname">{user.name}</div>
                <div className="nb-mobile-urole">
                  {isAdmin ? '🛡️ Admin' : isDecorator ? '🎨 Decorator' : '👤 Client'}
                </div>
              </div>
            </div>
          )}

          <div className="nb-mobile-links">
            <Link to="/decorators" className={`nb-mobile-link ${isActive('/decorators') ? 'nb-mobile-active' : ''}`}>
              <span>🎨</span> Decorators
            </Link>

            {user ? (
              <>
                <Link to={getDashboardLink()} className={`nb-mobile-link ${isActive(getDashboardLink()) ? 'nb-mobile-active' : ''}`}>
                  <span>📋</span> {getDashboardLabel()}
                </Link>
                <Link to="/chat" className={`nb-mobile-link ${isActive('/chat') ? 'nb-mobile-active' : ''}`}>
                  <span>💬</span> Chat
                </Link>
                <Link to="/notifications" className={`nb-mobile-link ${isActive('/notifications') ? 'nb-mobile-active' : ''}`}>
                  <span>🔔</span> Notifications
                  {unreadCount > 0 && <span className="nb-mobile-badge">{unreadCount}</span>}
                </Link>
                <Link to="/faq" className='nb-mobile-link'> <span>🤔</span>How It Works</Link>
                <div className="nb-mobile-divider" />
                <button className="nb-mobile-logout" onClick={handleLogout}>
                  🚪 Logout
                </button>
              </>
            ) : (
              <>
                <Link to="/login" className="nb-mobile-link"><span>🔑</span> Login</Link>
                <Link to="/register" className="nb-mobile-link nb-mobile-register">
                  <span>🚀</span> Register — Free
                </Link>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Overlay */}
      {menuOpen && <div className="nb-overlay" onClick={() => setMenuOpen(false)} />}
    </>
  );
}